import { FirebaseApp, initializeApp } from 'firebase/app'
import {
  Auth,
  AuthProvider,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  getFirestore,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import {
  FirebaseStorage,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
} from 'firebase/storage'
import Minze from 'minze'
import axios from 'redaxios'
import { config } from '../constants'
import { getFromCache, storeInCache } from './cache'
import { Emitter } from './emitter'
import { ConsoleError, ConsoleInfo } from './logging'
import MainModule from './main'
import { Storage } from './storage'
import {
  generateId,
  recursiveCleanObject,
  sanitizeFileName,
  unescapeHtmlProSpaceData,
} from './utils'

export enum FavoriteItemType {
  AllEvents = 'allEvents',
  Event = 'events',
  Exhibition = 'exhibitions',
  EventOffers = 'eventOffers',
  EventSeries = 'eventSeries',
  Artist = 'artist',
  Artists = 'artists',
  Contributors = 'contributors',
  Places = 'places',
}

export interface FavoriteItem {
  id: number
  type: FavoriteItemType
}

export class AuthModule {
  private _fbApp: FirebaseApp | null = null
  private _initialized: boolean = false
  private _firebaseConfig: any = config.firebase
  private _auth: Auth | null = null
  private _firestore: Firestore | null = null
  private _firebaseStorage: FirebaseStorage | null = null
  private _ee: Emitter | null = null
  private _main: MainModule | null = null
  private _storage: Storage = new Storage({ useSession: false, useRam: false })
  private _firedSuccessfulSignInEvent: boolean = false

  private _isLoggedIn: boolean = false
  private _user: any = null
  private _accessToken: string = ''
  private _favoritedItemWithCache: FavoriteItem | null = null

  constructor(ee: Emitter, main: MainModule) {
    this._ee = ee
    this._main = main
  }

  public async onAuthStateChanged(user: any): Promise<void> {
    if (user) {
      this._isLoggedIn = true
      this._user = user
      this._accessToken = user.accessToken
      this._favoritedItemWithCache = getFromCache('lvc:favorite_item', true)

      if (getFromCache('signingInWith')) {
        // it's impossible to not have _auth here as this function is triggerd by it
        //@ts-ignore
        await this.finishProviderSignIn(this._auth)
      }

      // check if the user favorited an item while not logged in and save it to his favorites
      if (this._favoritedItemWithCache) {
        let { id, type } = this._favoritedItemWithCache
        await this.saveItemToFavorites(id, type)
      }

      if (this._firestore) {
        try {
          const { uid } = user
          let ref = doc(this._firestore, `users/${uid}`)
          let favoritesCollection = collection(this._firestore, `users/${uid}/favorites`)
          const docSnap = await getDoc(ref)
          const favoritesSnap = await getDocs(favoritesCollection)
          if (docSnap.exists()) {
            const userData = docSnap.data()
            const favorites: FavoriteItem[] = []
            favoritesSnap.forEach(doc => {
              const data: { type: FavoriteItemType; id: number } = doc.data() as {
                type: FavoriteItemType
                id: number
              }
              favorites.push({ id: data.id, type: data.type })
            })
            Minze.cast('lvc:user-data-updated', { favorites: favorites })
            this._user = { ...user, profile: userData, favorites: favorites }
          }
        } catch (error) {
          ConsoleError(true, error)
        }
      }
    } else {
      this._isLoggedIn = false
      this._user = null
    }

    if (!this._initialized) {
      this._initialized = true
    }

    if (!this._firedSuccessfulSignInEvent) {
      this._ee?.emit('lvc:user_state_changed', {
        isLoggedIn: this._isLoggedIn,
        user: this._user,
      })
      // Also broadcast to window for React components and other window listeners
      Minze.cast('lvc:user_state_changed', {
        isLoggedIn: this._isLoggedIn,
        user: this._user,
      })
    }
  }

  private async updateUserObject(): Promise<void> {
    if (this.isLoggedIn() && this._firestore && this._user) {
      try {
        let ref = doc(this._firestore, `users/${this._user.uid}`)
        let favoritesCollection = collection(this._firestore, `users/${this._user.uid}/favorites`)
        const docSnap = await getDoc(ref)
        const favoritesSnap = await getDocs(favoritesCollection)
        if (docSnap.exists()) {
          const userData = docSnap.data()
          const favorites: FavoriteItem[] = []
          favoritesSnap.forEach(doc => {
            const data: { type: FavoriteItemType; id: number } = doc.data() as {
              type: FavoriteItemType
              id: number
            }
            favorites.push({ id: data.id, type: data.type })
          })
          this._user = {
            ...this._user,
            profile: userData,
            favorites: favorites,
          }
        }
      } catch (error) {
        ConsoleError(true, error)
      }
    }
  }

  public async getUserCustomDocument(path: string): Promise<unknown> {
    if (this.isLoggedIn() && this._firestore && this._user) {
      try {
        let docRef = doc(this._firestore, `users/${this._user.uid}/${path}`)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          return docSnap.data()
        }
      } catch (error) {
        ConsoleError(true, error)
      }
    }
    return undefined
  }

  public async setUserCustomDocument(path: string, data: unknown): Promise<boolean> {
    if (this.isLoggedIn() && this._firestore && this._user) {
      try {
        let docRef = doc(this._firestore, `users/${this._user.uid}/${path}`)
        const docSnap = await setDoc(docRef, data as any, { merge: true })
        return true
      } catch (error) {
        console.error('Error when setting user custom document:', error)
        return false
      }
    }
    return false
  }

  public async deleteUserCustomDocument(path: string): Promise<boolean> {
    if (this.isLoggedIn() && this._firestore && this._user) {
      try {
        let docRef = doc(this._firestore, `users/${this._user.uid}/${path}`)
        await deleteDoc(docRef)
        return true
      } catch (error) {
        ConsoleError(true, error)
        return false
      }
    }
    return false
  }

  public async initialize(ee?: Emitter): Promise<void> {
    if (ee) {
      this._ee = ee
    }

    if (!this._fbApp) {
      const config = {
        ...this._firebaseConfig,
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
      }
      this._fbApp = initializeApp(config)
    }
    if (!this._auth) {
      this._auth = getAuth(this._fbApp)

      onAuthStateChanged(this._auth, this.onAuthStateChanged.bind(this))
    }

    if (!this._firestore) {
      this._firestore = getFirestore(this._fbApp)
    }

    if (!this._firebaseStorage) {
      this._firebaseStorage = getStorage(this._fbApp)
    }
  }

  private async hasUserProfileDoc(uid: string): Promise<boolean> {
    if (this._firestore) {
      const ref = doc(this._firestore, `users/${uid}`)
      const docSnap = await getDoc(ref)

      return docSnap.exists()
    } else {
      return false
    }
  }

  public async login(
    email: string,
    password: string,
    goToAccount = false,
    redirectTo?: string
  ): Promise<any> {
    if (this._auth) {
      try {
        let response = await signInWithEmailAndPassword(this._auth, email, password)
        this._ee?.emit('lvc:user_signed_but_not_fully_loaded')
        let token = await response.user.getIdToken()
        await MainModule.getInstance().analyticsTrack('login', {
          method: 'Email',
          user_id: response?.user?.uid || null,
        })
        await this.finishSignIn({ accessToken: token }, goToAccount, redirectTo)
      } catch (error: any) {
        return error
      }
    }
  }

  private async signInProviderWithPopup(
    auth: Auth,
    provider: AuthProvider,
    goToAccount = false,
    redirectTo?: string
  ): Promise<void> {
    try {
      const response = await signInWithPopup(auth, provider)
      if (response && response.user) {
        const options = {
          lang: MainModule.getInstance().getLocale().getLanguage(),
        }
        const completeSignupResponse = await this.completeSignup(options) // TODO: add skip if signupCompleted is true  handles user doc creation
        if (completeSignupResponse?.success) {
          const token = await response.user.getIdToken()
          await this.finishSignIn({ accessToken: token }, goToAccount, redirectTo)
        } else {
          throw new Error('Failed to fully complete signup')
        }
      }
    } catch (error) {
      ConsoleError(true, error)
      this._ee?.emit('lvc:finished_signing_in:failed')
    }
  }

  public async callGoogleSignIn(goToAccount = false, redirectTo?: string): Promise<void> {
    if (this._auth) {
      let provider = new GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'consent',
      })
      await this.signInProviderWithPopup(this._auth, provider, goToAccount, redirectTo)
    }
  }

  public async callFacebookSignIn(goToAccount = false, redirectTo?: string): Promise<void> {
    if (this._auth) {
      let provider = new FacebookAuthProvider()
      provider.setCustomParameters({
        prompt: 'consent',
      })
      await this.signInProviderWithPopup(this._auth, provider, goToAccount, redirectTo)
    }
  }

  private async finishSignIn(
    credential: any,
    goToAccount = false,
    redirectTo?: string
  ): Promise<any> {
    try {
      if (!credential || !credential.accessToken) throw new Error('no access token')

      const resp = await axios.post(
        `${config.backendApi}/user/auth/tag-login`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )
      this._ee?.emit('lvc:finished_signing_in:success', {
        goToAccount: goToAccount,
        redirectTo: redirectTo,
      })
      this._firedSuccessfulSignInEvent = true
      return resp.data
    } catch (error) {
      ConsoleError(true, error)
      this._ee?.emit('lvc:finished_signing_in:failed')
    }
  }

  public async completeSignup(options: any = {}) {
    const {
      lang,
      firstName,
      lastName,
      zipcode,
      region,
      acceptNotifications,
      subscribeNewsletter,
      policyAccepted,
      termsAccepted,
      ageAccepted,
    } = options

    const email = this.getUserAuth()?.email // get the email from the firebase user object
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/complete-account`,
        {
          userInfo: {
            policyAccepted,
            termsAccepted,
            ageAccepted,
            personalData: {
              email,
              firstName,
              lang,
              lastName,
              zipcode,
              region,
            },
            notifications: {
              upcomingEvents: acceptNotifications,
              favorites: acceptNotifications,
              newsletter: subscribeNewsletter,
            },
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )
      let provider = this.getUserAuth()?.providerData?.[0]?.providerId
      let method_string = ''

      switch (provider) {
        case 'facebook.com':
          method_string = 'Facebook'
          break
        case 'google.com':
          method_string = 'Google'
          break
        default:
          method_string = 'Email'
      }
      await MainModule.getInstance().analyticsTrack('sign_up', {
        method: method_string,
      })
      return result.data
    } catch (error) {}
  }

  public async createUser(email: string, password: string, options: any = {}) {
    const {
      lang,
      acceptNotifications,
      subscribeNewsletter,
      policyAccepted,
      termsAccepted,
      ageAccepted,
    } = options
    try {
      const result = await axios.post(`${config.backendApi}/user/auth/register`, {
        userAuth: {
          email,
          password,
          displayName: '',
        },
        userInfo: {
          policyAccepted,
          termsAccepted,
          ageAccepted,
          personalData: {
            lang,
          },
          notifications: {
            upcomingEvents: acceptNotifications,
            favorites: acceptNotifications,
            newsletter: subscribeNewsletter,
          },
        },
      })

      if (result.data?.success) {
        this._ee?.emit('lvc:user_signed_but_not_fully_loaded')
      }

      await MainModule.getInstance().analyticsTrack('sign_up', {
        method: 'Email',
      })

      return result.data
    } catch (error: any) {
      if (error?.data) {
        return {
          success: false,
          error: error.data,
        }
      }
      return {
        success: false,
      }
    }
  }

  public async loadProfile(): Promise<any> {
    if (this._firestore && this._user) {
      try {
        const { uid } = this._user
        let ref: any = doc(this._firestore, `users/${uid}`)
        let snap: any = await getDoc(ref)
        return snap.data()
      } catch (e) {
        return null
      }
    }
    return null
  }

  public async saveItemToFavorites(id: number, type: FavoriteItemType) {
    if (this._firestore && this._user && this._isLoggedIn) {
      try {
        const { uid } = this._user
        let ref = doc(this._firestore, `users/${uid}/favorites/${type + id}`)
        const docSnap = await getDoc(ref)
        if (docSnap.exists()) {
          await deleteDoc(ref)
          this._user.favorites = [...this._user.favorites].filter(
            (item: FavoriteItem) => item.id !== id || item.type !== type
          )
          Minze.cast('lvc:item-favorite-status-update', {
            type: type,
            id: id,
            favorite: false,
            favorites: this._user?.favorites,
          })
        } else {
          const data = { id, type }
          await setDoc(ref, data)
          this._user.favorites = [...this._user.favorites, { id: id, type: type }]
          Minze.cast('lvc:item-favorite-status-update', {
            type: type,
            id: id,
            favorite: true,
            favorites: this._user?.favorites,
          })
        }
      } catch (error) {
        ConsoleError(true, error)
      }
    } else {
      MainModule.getInstance().injectIntoDOM('lvc-not-logged-in-popup')
      storeInCache('lvc:favorite_item', { id, type }, 300000) // 5 minutes retention
    }
  }

  public async saveProfile(category: string, value: any): Promise<void> {
    if (this._firestore && this._user) {
      try {
        const { uid } = this._user
        const previous = await this.loadProfile()
        let ref = doc(this._firestore, `users/${uid}`)
        let data = {}
        if (category === 'profile-culture') {
          data = {
            culturalProfile: {
              ...previous.culturalProfile,
              ...value,
            },
          }
        } else if (category === 'profile-personal') {
          data = {
            personalData: {
              ...previous.personalData,
              ...value,
            },
          }
        } else if (category === 'profile-notification') {
          data = {
            notifications: {
              favorites: value.favorites,
              newsletter: value.newsletter,
              upcomingEvents: value.upcomingEvents,
            },
          }
          // update the brevo contact to add notifications preferences
          await this.saveNotificationPreferences(
            value.favorites,
            value.newsletter,
            value.upcomingEvents
          )
        } else if (category === 'user-process') {
          data = {
            userProcess: {
              ...previous.userProcess,
              ...value,
            },
          }
        } else if (category === 'partnerData') {
          data = {
            partnerData: {
              ...previous.partnerData,
              ...value,
            },
          }
        }

        await updateDoc(ref, data)
        await this.updateUserObject()
      } catch (error) {
        ConsoleError(true, error)
      }
    } else {
    }
  }

  // function to save notification preferences in the brevo contact via the backend
  public async saveNotificationPreferences(
    favorites: boolean,
    newsletter: boolean,
    upcomingEvents: boolean
  ): Promise<void> {
    if (this._firestore && this._user) {
      try {
        const result = await axios.post(
          `${config.backendApi}/user/auth/save-notification-preferences`,
          {
            favorites,
            newsletter,
            upcomingEvents,
          },
          {
            headers: {
              Authorization: `Bearer ${this._accessToken}`,
            },
          }
        )
        return result.data
      } catch (error) {
        ConsoleError(true, error)
      }
    }
  }

  public async deleteAccount(reason: string = 'none'): Promise<void> {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/delete-account`,
        {
          reason: reason,
        },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )

      return result.data
    } catch (error) {
      return
    }
  }

  public async changeEmail(email: string): Promise<void> {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/change-email`,
        { email: email },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )

      return result.data
    } catch (error) {
      return
    }
  }

  public async createPassword() {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/create-password`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )

      return result.data
    } catch (error) {
      return
    }
  }

  public async becomePartner(partnerData: any) {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/become_partner`,
        { ...partnerData },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )
      return result.data
    } catch (error: any) {
      if (error?.data) {
        return {
          success: false,
          error: error.data,
        }
      }
      return {
        success: false,
      }
    }
  }
  /**
   * Creates a new content item (event, exhibition, or festival) by:
   * 1. Preparing the item data with creation timestamp and sanitization
   * 2. Refreshing the user's authentication token
   * 3. Saving the complete item data to the user's "created_items" collection
   * 4. Registering the item with the backend API
   * 5. Removing the draft version from the user's drafts collection
   */
  public async createContentItem(
    id: string,
    itemData: any,
    itemType: 'event' | 'exhibition' | 'festival'
  ): Promise<{ success: boolean; message?: string }> {
    try {
      // 1. Prepare data
      const draftData = { ...itemData, createdAt: Date.now() }
      const sanitizedData = unescapeHtmlProSpaceData(draftData)
      const mainModule = MainModule.getInstance()

      // 2. Refresh token (forms can take a long time to fill)
      const user = await this.getFirebaseAuth().currentUser
      this._accessToken = await user.getIdToken(true)

      // 3. Save document to created_items collection
      const saveSuccess = await mainModule
        .getAuth()
        .setUserCustomDocument(`created_${itemType}s/${id}`, recursiveCleanObject(sanitizedData))

      if (!saveSuccess) {
        return {
          success: false,
          message: `Failed to save ${itemType}. Please try again.`,
        }
      }

      // 4. Send content to backend
      try {
        await this.sendItemToBackend(id, itemType)
      } catch (error) {
        await mainModule.getAuth().setUserCustomDocument(`created_${itemType}s/${id}`, {
          consoImportError: (error as any).data.status || 'Unknown error',
        })

        return {
          success: false,
          message: `Error when creating ${itemType} on the backend`,
        }
      }

      // 5. Delete draft
      const deleteSuccess = await mainModule
        .getAuth()
        .deleteUserCustomDocument(`${itemType}_drafts/${id}`)

      if (!deleteSuccess) {
        console.warn(`Failed to delete ${itemType} draft ${id}, but item was created successfully`)
        // We don't fail the whole operation if just the draft deletion fails
      }

      return { success: true }
    } catch (error) {
      console.error(`Unexpected error during ${itemType} creation:`, error)
      return {
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      }
    }
  }

  /**
   * Attempts to send item to the backend server
   */
  private async sendItemToBackend(
    id: string,
    itemType: 'event' | 'exhibition' | 'festival'
  ): Promise<boolean> {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/create_${itemType}`,
        { entryId: id },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )

      return result.status === 200
    } catch (error) {
      console.error(`Error when calling the backend for ${itemType} creation:`, error)
      return false
    }
  }

  /**
   * Creates a new contribution by linking a published contributor to one or more
   * published events with a specific contribution type. This is for adding missing
   * contributions to live content.
   *
   * The process:
   * 1. Prepares the contribution data with creation timestamp and sanitization
   * 2. Refreshes the user's authentication token
   * 3. Saves the contribution data to the user's "created_contributions" collection
   * 4. Sends the contribution to the backend API for Supabase import
   *
   * @param id - Unique identifier for this contribution batch
   * @param contributionData - Contribution data with numeric IDs (backend formats to "LVC-PROD-ID-{numericId}")
   * @returns Success status with optional error message
   */
  public async createContribution(
    id: string,
    contributionData: {
      contributorId: number
      contributionType: string
      contributions: Array<{
        eventId?: number
        eventOfferId?: number
        eventSeriesId?: number
        exhibitionEventId?: number
      }>
    }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      // 1. Prepare data
      const data = {
        ...contributionData,
        createdAt: Date.now(),
      }
      const sanitizedData = unescapeHtmlProSpaceData(data)
      const mainModule = MainModule.getInstance()

      // 2. Refresh token (forms can take time to fill)
      const user = await this.getFirebaseAuth().currentUser
      this._accessToken = await user.getIdToken(true)

      // 3. Save document to created_contributions collection
      const saveSuccess = await mainModule
        .getAuth()
        .setUserCustomDocument(`created_contributions/${id}`, recursiveCleanObject(sanitizedData))

      if (!saveSuccess) {
        return {
          success: false,
          message: 'Failed to save contribution. Please try again.',
        }
      }

      // 4. Send to backend
      try {
        await this.sendContributionToBackend(id)
      } catch (error) {
        await mainModule.getAuth().setUserCustomDocument(`created_contributions/${id}`, {
          consoImportError: (error as any).data?.status || 'Unknown error',
        })

        return {
          success: false,
          message: 'Error when creating contribution on the backend',
        }
      }

      return { success: true }
    } catch (error) {
      console.error('Unexpected error during contribution creation:', error)
      return {
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      }
    }
  }

  /**
   * Sends contribution data to the backend server
   */
  private async sendContributionToBackend(id: string): Promise<boolean> {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/create_contribution`,
        { entryId: id },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )

      return result.status === 200
    } catch (error) {
      console.error('Error calling backend for contribution creation:', error)
      return false
    }
  }

  /**
   * Creates a new contributor by saving data to Firestore and sending to backend
   * @param id - Unique identifier for the contributor document
   * @param contributorData - Contributor data including names, descriptions, image, and optional address
   * @returns Success status with optional error message
   */
  public async createContributor(
    id: string,
    contributorData: {
      contributorId: string
      contributorType: string
      nameFr: string
      nameEn: string
      descriptionFr: string
      descriptionEn: string
      alternateName: string
      mainEntityOfPageFr: string
      mainEntityOfPageEn: string
      nationality: string
      birthplace: string
      workplace: string
      wikidataId: string
      isni: string
      shopifyUrl: string
      appleUrl: string
      youtubeUrl: string
      postalAddress?: {
        streetAddress?: string
        addressLocality?: string
        addressRegion?: string
        postalCode?: string
      }
      imageUrl: string
      royaltyCheckbox: boolean
      imageCredit?: string
      altTextFr?: string
      altTextEn?: string
    }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      // 1. Prepare data
      const data = {
        ...contributorData,
        createdAt: Date.now(),
      }
      const sanitizedData = unescapeHtmlProSpaceData(data)
      const mainModule = MainModule.getInstance()

      // 2. Refresh token (forms can take time to fill)
      const user = await this.getFirebaseAuth().currentUser
      this._accessToken = await user.getIdToken(true)

      // 3. Save document to created_contributors collection
      const saveSuccess = await mainModule
        .getAuth()
        .setUserCustomDocument(`created_contributors/${id}`, recursiveCleanObject(sanitizedData))

      if (!saveSuccess) {
        return {
          success: false,
          message: 'Failed to save contributor. Please try again.',
        }
      }

      // 4. Send to backend
      try {
        await this.sendContributorToBackend(id)
      } catch (error) {
        await mainModule.getAuth().setUserCustomDocument(`created_contributors/${id}`, {
          consoImportError: (error as any).data?.status || 'Unknown error',
        })

        return {
          success: false,
          message: 'Error when creating contributor on the backend',
        }
      }

      return { success: true }
    } catch (error) {
      console.error('Unexpected error during contributor creation:', error)
      return {
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      }
    }
  }

  /**
   * Sends contributor data to the backend server
   */
  private async sendContributorToBackend(id: string): Promise<boolean> {
    try {
      const result = await axios.post(
        `${config.backendApi}/user/auth/create_contributor`,
        { entryId: id },
        {
          headers: {
            Authorization: `Bearer ${this._accessToken}`,
          },
        }
      )

      return result.status === 200
    } catch (error) {
      console.error('Error calling backend for contributor creation:', error)
      return false
    }
  }

  /* function to get the draft events of the user */
  public async fetchDraftEvents(): Promise<any> {
    if (this._firestore && this._user) {
      try {
        let draftsCollectionRef = collection(
          this._firestore,
          `users/${this._user.uid}/event_drafts`
        )

        const querySnapshot = await getDocs(draftsCollectionRef)

        const draftEvents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))

        return draftEvents
      } catch (error) {
        ConsoleError(true, error)
      }
    }
    return []
  }

  /* function to fetch a specific draft event by its id */
  public async fetchDraftEventById(draftId: string): Promise<any | null> {
    if (this._firestore && this._user) {
      try {
        let draftDocRef = doc(this._firestore, `users/${this._user.uid}/event_drafts/${draftId}`)

        const docSnap = await getDoc(draftDocRef)

        if (docSnap.exists()) {
          return docSnap.data()
        }
      } catch (error) {
        ConsoleError(true, error)
      }
    }
  }

  /* function to delete a draft event/exhibition/festival by its id */
  public async deleteDraftItem(
    draftId: string,
    collection: 'event_drafts' | 'exhibition_drafts' | 'festival_drafts'
  ): Promise<void> {
    if (this._firestore && this._user) {
      try {
        const draftDocRef = doc(this._firestore, `users/${this._user.uid}/${collection}/${draftId}`)
        await deleteDoc(draftDocRef)
      } catch (error) {
        ConsoleError(true, error)
      }
    }
  }

  /* Function to fetch all draft exhibitions for the current user */
  public async fetchDraftExhibitions(): Promise<any> {
    if (this._firestore && this._user) {
      try {
        const draftsCollectionRef = collection(
          this._firestore,
          `users/${this._user.uid}/exhibition_drafts`
        )
        const querySnapshot = await getDocs(draftsCollectionRef)
        const draftExhibitions = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        return draftExhibitions
      } catch (error) {
        ConsoleError(true, error)
      }
    }
    return []
  }

  /* Function to fetch a specific draft exhibition by its ID */
  public async fetchDraftExhibitionById(draftId: string): Promise<any | null> {
    if (this._firestore && this._user) {
      try {
        const draftDocRef = doc(
          this._firestore,
          `users/${this._user.uid}/exhibition_drafts/${draftId}`
        )
        const docSnap = await getDoc(draftDocRef)

        if (docSnap.exists()) {
          return docSnap.data()
        }
      } catch (error) {
        ConsoleError(true, error)
      }
    }
  }

  /* Function to fetch all draft festivals for the current user */
  public async fetchDraftFestivals(): Promise<any> {
    if (this._firestore && this._user) {
      try {
        const draftsCollectionRef = collection(
          this._firestore,
          `users/${this._user.uid}/festival_drafts`
        )
        const querySnapshot = await getDocs(draftsCollectionRef)
        const draftFestivals = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        return draftFestivals
      } catch (error) {
        ConsoleError(true, error)
      }
    }
    return []
  }

  /* Function to fetch a specific draft festival by its ID */
  public async fetchDraftFestivalById(draftId: string): Promise<any | null> {
    if (this._firestore && this._user) {
      try {
        const draftDocRef = doc(
          this._firestore,
          `users/${this._user.uid}/festival_drafts/${draftId}`
        )
        const docSnap = await getDoc(draftDocRef)

        if (docSnap.exists()) {
          return docSnap.data()
        }
      } catch (error) {
        ConsoleError(true, error)
      }
    }
  }

  public async uploadAnonymousImage(imageFile: File): Promise<string> {
    if (!this._initialized) throw new Error('auth not initialized')

    return await this.uploadImageInternal(`user_content/anonymous`, imageFile)
  }
  // Define a type for the progress observer callback
  public async uploadResumableAnonymousImage(
    imageFile: File,
    progressObserver?: (progress: any) => void
  ): Promise<string> {
    if (!this._initialized) throw new Error('auth not initialized')

    return await this.uploadResumableImageInternal(
      `user_content/anonymous`,
      imageFile,
      progressObserver
    )
  }

  public async uploadImage(imageFile: File): Promise<string> {
    if (!this._initialized) throw new Error('auth not initialized')

    if (!this.isLoggedIn()) throw new Error('user not logged in')

    let userId = this._user.uid
    return await this.uploadImageInternal(`user_content/personal/${userId}`, imageFile)
  }

  /*
   * Uploads an image to storage (ensure persmissions are given)
   * Try catch is not at this level, please use it as per case basis.
   */
  private async uploadImageInternal(pathPrefix: string, imageFile: File): Promise<string> {
    const filePath = `${pathPrefix}/${generateId()}_${sanitizeFileName(imageFile.name)}`

    if (!this._firebaseStorage) throw new Error('no firebase storage')

    const storageRef = ref(this._firebaseStorage, filePath)
    const snapshot = await uploadBytes(storageRef, imageFile)

    if (!snapshot) throw new Error('no image uploaded')

    return filePath
  }
  private async uploadResumableImageInternal(
    pathPrefix: string,
    imageFile: File,
    progressObserver?: (progress: number) => void
  ): Promise<string> {
    const filePath = `${pathPrefix}/${generateId()}_${sanitizeFileName(imageFile.name)}`

    if (!this._firebaseStorage) throw new Error('no firebase storage')

    const storageRef = ref(this._firebaseStorage, filePath)
    const uploadTask = uploadBytesResumable(storageRef, imageFile)
    // Register three observers:
    // 1. 'state_changed' observer, called any time the state changes
    // 2. Error observer, called on failure
    // 3. Completion observer, called on successful completion
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        snapshot => {
          // Observe state change events such as progress, pause, and resume
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          if (progressObserver) {
            // Ensure progress is a number
            progressObserver(progress)
          }
        },
        error => {
          // Handle unsuccessful uploads
          reject(error)
        },
        () => {
          // Handle successful uploads on complete
          resolve(filePath)
        }
      )
    })
  }

  public async getImageUrl(imagePath: string): Promise<string> {
    if (!this._initialized) throw new Error('auth not initialized')

    if (!this.isLoggedIn()) throw new Error('user not logged in')

    if (!this._firebaseStorage) throw new Error('no firebase storage')

    let imageRef = ref(this._firebaseStorage, imagePath)

    return await getDownloadURL(imageRef)
  }

  public getFirebaseAuth(): any {
    return this._auth
  }
  public async b2bLogout(redirectTo = '/'): Promise<void> {
    if (this._auth) {
      await signOut(this._auth)

      window.location.href = redirectTo
    }
  }

  public async logout(): Promise<void> {
    if (this._auth) {
      await signOut(this._auth)
      // remove the flag that the user has seen the popup so it shows again when he logs back in
      sessionStorage.removeItem('hasSeenCompleteProfilePopup')
      this._ee?.emit('lvc:user_logged_out')
      // Also broadcast to window for React components and other window listeners
      Minze.cast('lvc:user_logged_out')
    }
  }

  public isSignInWithRedirect(): boolean {
    let signingInWith = getFromCache('signingInWith')

    return !!signingInWith
  }

  public isLoggedIn(): boolean {
    return this._isLoggedIn
  }

  public getUserData(): any {
    if (this._isLoggedIn) {
      return this._user
    } else return null
  }
  public getUserAuth(): any {
    if (this._isLoggedIn) {
      return this._auth?.currentUser || null
    } else return null
  }

  // function to check if the user completed his profile (personal data, notifications, etc)
  public async isProfileCompleted(): Promise<boolean> {
    if (this._isLoggedIn) {
      let user = await this.loadProfile()
      // check if the user completed the profile or if he skipped the completion
      if (user.userProcess.signupCompleted || user.userProcess?.signupCompletionSkipped) {
        return true
      } else {
        return false
      }
    }
    return true
  }

  public async isPartner(): Promise<boolean> {
    if (this._isLoggedIn) {
      let user = await this.getFirebaseAuth().currentUser.getIdTokenResult()
      ConsoleInfo(true, 'claims ', user.claims)
      return user.claims.partner
    }
    return false
  }

  public async isPartnerInProcess(): Promise<boolean> {
    if (this._isLoggedIn) {
      let user = await this.loadProfile()
      return user && typeof user.becomePartnerProcessStarted !== 'undefined'
        ? !!user.becomePartnerProcessStarted
        : false
    }
    return false
  }

  public async getPartnerData(): Promise<any> {
    if (this._isLoggedIn) {
      return this._user.profile.partnerData
    } else return null
  }

  savePartnerData(data: any): void {
    this.saveProfile('partnerData', data)
  }

  public async subscribeToB2bNewsletter(email: string): Promise<void> {
    try {
      await axios.post(`${config.backendApi}/user/public/contact_news_b2b`, {
        email,
        lang: this.getUserLanguage(),
      })
    } catch (error) {
      ConsoleError(true, error)
    }
  }

  public getFavorites(): any[] {
    if (this._isLoggedIn) {
      return this._user.favorites || []
    }

    return []
  }

  public getUserLanguage(): string | null {
    if (this._isLoggedIn) {
      return this.getUserData()?.profile?.personalData?.lang ?? null
    }

    return null
  }

  public getAccessToken(): string {
    return this._accessToken
  }
}
